import BookCallCTA from '../components/BookCallCTA';
import { useSeo } from '../hooks/useSeo';

export default function AboutPage() {
  useSeo({
    title: 'About Noah Blanchard & Point Break Systems',
    description: "Point Break Systems builds AI ISA replacement systems for real estate professionals. Founded by Noah Blanchard in Newfoundland, Canada. Custom CRM integrations, bilingual calling, and systems built on client-owned accounts.",
    path: '/about',
    schemas: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        "name": "About Point Break Systems",
        "description": "Point Break Systems builds custom AI voice caller systems for real estate professionals. Built around client workflows, with bilingual calling and client-owned accounts.",
        "url": "https://pointbreak.systems/about",
        "mainEntity": {
          "@type": "Organization", "@id": "https://pointbreak.systems/#organization",
          "name": "Point Break Systems",
          "url": "https://pointbreak.systems",
          "foundingDate": "2024",
          "foundingLocation": { "@type": "Place", "name": "Newfoundland, Canada" },
          "areaServed": ["Canada", "United States"],
          "knowsAbout": ["AI Voice Callers", "Real Estate Automation", "Lead Qualification", "CASL Compliance", "TCPA Compliance", "CRM Integration", "Bilingual AI Calling"],
          "founder": {
            "@type": "Person", "@id": "https://pointbreak.systems/about#noah-blanchard",
            "name": "Noah Blanchard",
            "jobTitle": "Founder",
            "url": "https://pointbreak.systems/about",
            "worksFor": { "@type": "Organization", "@id": "https://pointbreak.systems/#organization", "name": "Point Break Systems" }
          }
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "Person", "@id": "https://pointbreak.systems/about#noah-blanchard",
        "name": "Noah Blanchard",
        "jobTitle": "Founder",
        "url": "https://pointbreak.systems/about",
        "telephone": "+17096498492",
        "email": "noah@pointbreak.systems",
        "address": {
          "@type": "PostalAddress",
          "addressRegion": "NL",
          "addressCountry": "CA"
        },
        "worksFor": {
          "@type": "Organization", "@id": "https://pointbreak.systems/#organization",
          "name": "Point Break Systems",
          "url": "https://pointbreak.systems"
        },
        "knowsAbout": [
          "AI Voice Calling",
          "Real Estate Lead Automation",
          "CASL Compliance",
          "TCPA Compliance",
          "CRM Integration",
          "Bilingual AI Calling"
        ],
        "sameAs": [
          "https://www.linkedin.com/in/foundernoahblanchard/"
        ]
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://pointbreak.systems" },
          { "@type": "ListItem", "position": 2, "name": "About", "item": "https://pointbreak.systems/about" }
        ]
      },
    ],
  });

  return (
    <div className="bg-white">
      <section className="max-w-5xl mx-auto px-5 sm:px-6 pt-8 pb-8 sm:pt-14 sm:pb-10">
        <p className="text-sm font-semibold text-gray-500 mb-3">About Point Break Systems</p>
        <h1 className="text-3xl sm:text-5xl font-bold leading-tight text-gray-900 max-w-3xl mb-5">Better follow-up. Built around your business.</h1>
        <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mb-6">We build custom AI callers for real estate teams across Canada and the US, so new inquiries and old leads get consistent follow-up.</p>
        <BookCallCTA />
      </section>
      <section id="our-story" className="bg-[#fdfcf9] py-8 sm:py-12">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 grid md:grid-cols-2 gap-8 md:gap-12">
          <div><h2 className="text-2xl font-bold text-gray-900 mb-4">Why we started</h2><p className="text-base leading-relaxed text-gray-600">Founded in Newfoundland in 2024, Point Break Systems started with a familiar problem: good leads going cold while agents were busy. We connect the calling, qualification, and booking work to the tools your team already uses.</p></div>
          <dl className="text-base">
            {[['Based in', 'Newfoundland, Canada'], ['Working with', 'Canadian and US real estate teams'], ['Your system', 'Built on your own accounts'], ['Our role', 'Build, integrate, test, and support']].map(([label, value]) => <div key={label} className="border-t border-gray-200 py-3 grid grid-cols-[1fr_2fr] gap-4"><dt className="text-gray-500">{label}</dt><dd className="font-medium text-gray-900">{value}</dd></div>)}
          </dl>
        </div>
      </section>
      <section id="leadership" className="max-w-5xl mx-auto px-5 sm:px-6 py-8 sm:py-12">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <img src="/media/noah-256.webp" srcSet="/media/noah-128.webp 128w, /media/noah-256.webp 256w" sizes="112px" decoding="async" alt="Noah Blanchard, founder of Point Break Systems" width="112" height="112" loading="lazy" className="w-28 h-28 rounded-lg object-cover" />
          <div className="max-w-2xl"><h2 id="noah-blanchard" className="text-2xl font-bold text-gray-900">Noah Blanchard</h2><p className="text-sm text-gray-500 mt-1 mb-3">Founder</p><p className="text-base text-gray-600 leading-relaxed">Noah works with clients on their call flows, CRM integrations, and ongoing improvements. Each system is trained around the team's scripts, lead sources, and qualification criteria.</p><div className="flex flex-wrap gap-x-6 gap-y-3 mt-4 text-sm"><a className="underline underline-offset-4 text-gray-700" href="mailto:noah@pointbreak.systems">noah@pointbreak.systems</a><a className="underline underline-offset-4 text-gray-700" href="tel:+17096498492">(709) 649-8492</a></div></div>
        </div>
      </section>
      <section className="border-t border-gray-200 py-8 sm:py-10"><div className="max-w-5xl mx-auto px-5 sm:px-6 flex flex-col sm:flex-row sm:items-center justify-between gap-5"><div><h2 className="text-2xl font-bold text-gray-900">Your accounts. Your system.</h2><p className="text-base text-gray-600 mt-2">One setup fee, plus an ongoing support and maintenance retainer.</p></div><BookCallCTA className="self-start shrink-0" /></div></section>
    </div>
  );
}
